import React, { memo } from 'react';
import PropTypes from 'prop-types';
import Skeleton from '../skeleton/Skeleton';

/**
 * Experience Section Component
 * Renders work experience information with loading and error states
 */
const ExperienceSection = memo(({ experiences, isLoading, error }) => {
    if (isLoading) {
        return <Skeleton type="resume" />;
    }

    if (error) {
        return (
            <div className="alert alert-warning" role="alert">
                <p className="mb-0">Unable to load experience data. Please try again later.</p>
            </div>
        );
    }

    if (!experiences?.length) {
        return (
            <div className="text-muted text-center py-4">
                <p className="mb-0">No experience information available.</p>
            </div>
        );
    }

    const formatDuration = (duration, current) => {
        if (!duration) return '';
        return current === '1' ? `${duration} - Current` : duration;
    };

    return (
        <>
            {experiences.map(({ id, designation, title, duration, excerpt, current }) => (
                <article className="experiences" key={id}>
                    <h3 className="text-5 mb-2">{designation}</h3>
                    <h6 className="mb-2 text-primary">
                        {title && (
                            <>
                                <span aria-label="Company">{title}</span>
                                {duration && ' / '}
                            </>
                        )}
                        {duration && (
                            <span className="text-secondary" aria-label="Duration">
                                {formatDuration(duration, current)}
                            </span>
                        )}
                    </h6>
                    {excerpt && <p className="mb-0 text-3">{excerpt}</p>}
                    <hr className="my-4" />
                </article>
            ))}
        </>
    );
});

ExperienceSection.displayName = 'ExperienceSection';

ExperienceSection.propTypes = {
    experiences: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            designation: PropTypes.string,
            title: PropTypes.string,
            duration: PropTypes.string,
            excerpt: PropTypes.string,
            current: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        })
    ),
    isLoading: PropTypes.bool.isRequired,
    error: PropTypes.string
};

ExperienceSection.defaultProps = {
    experiences: [],
    error: null
};

export default ExperienceSection;